import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';

interface Option {
  label: string;
  value: string | number;
}

interface SelectProps {
  label?: string;
  options: Option[];
  value?: any;
  onChange: (value: any) => void;
  placeholder?: string;
  multiple?: boolean;
  searchable?: boolean;
  onSearch?: (query: string) => void;
  error?: string;
  className?: string;
  required?: boolean;
}

export const Select = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select option...',
  multiple = false,
  searchable = true,
  onSearch,
  error,
  className = '',
  required = false,
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, dropUp: false });

  const updateDimensions = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldDropUp = spaceBelow < 280;

      setCoords({
        top: shouldDropUp
          ? rect.top + window.scrollY
          : rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        dropUp: shouldDropUp
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateDimensions();
      window.addEventListener('resize', updateDimensions);
      window.addEventListener('scroll', updateDimensions, true);
    }
    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('scroll', updateDimensions, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        const portalMenu = document.getElementById('portal-select-dropdown');
        if (portalMenu && portalMenu.contains(event.target as Node)) return;

        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : [value];

  const handleSelect = (val: string | number) => {
    if (multiple) {
      const newValue = selectedValues.includes(val)
        ? selectedValues.filter(v => v !== val)
        : [...selectedValues, val];
      onChange(newValue);
    } else {
      onChange(val);
      setIsOpen(false);
    }
  };

  const getLabel = () => {
    if (multiple) {
      if (selectedValues.length === 0) return placeholder;
      if (selectedValues.length === 1) return options.find(o => o.value === selectedValues[0])?.label || placeholder;
      return `${selectedValues.length} selected`;
    }
    return options.find(o => o.value === value)?.label || placeholder;
  };

  return (
    <div className={`flex flex-col gap-1.5 w-full relative ${className}`} ref={containerRef}>
      {label && (
        <label className="text-xs font-semibold text-slate-900 tracking-normal ml-1 block mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center justify-between h-8 w-full rounded-lg border bg-white px-3 py-1 text-xs cursor-pointer transition-all
          ${isOpen ? 'ring-2 ring-[#8dc63f]/20 border-[#8dc63f] shadow-sm' : 'border-slate-200'}
          ${error ? 'border-red-500 ring-red-500/20' : ''}
        `}
      >
        <span className={`truncate ${!value && selectedValues.length === 0 ? 'text-slate-300 opacity-40 font-medium' : 'text-slate-900 font-semibold'}`}>
          {getLabel()}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#8dc63f]' : ''}`} />
      </div>

      {isOpen && createPortal(
        <div
          id="portal-select-dropdown"
          className="fixed z-[999] bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
          style={{
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            width: `${coords.width}px`,
            transform: coords.dropUp ? 'translateY(-100%) translateY(-4px)' : 'translateY(4px)',
          }}
        >
          {/* Header row containing Search Input, Clear Action, and your Done button */}
          {(searchable || multiple) && (
            <div className="p-2 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
              {searchable && (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Search className="w-4 h-4 text-[#8dc63f] ml-1 shrink-0" />
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search..."
                    className="w-full bg-transparent border-none outline-none text-sm py-1 focus:ring-0 min-w-0"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      onSearch?.(e.target.value);
                    }}
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="p-1 hover:bg-slate-200 rounded-full transition-colors shrink-0">
                      <X className="w-3 h-3 text-slate-400" />
                    </button>
                  )}
                </div>
              )}

              {/* Your integrated Done Button */}
              {multiple && (
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-xs font-bold text-[#8dc63f] hover:text-[#76ab32] px-2.5 py-1 rounded-lg transition-colors whitespace-nowrap border border-slate-200 hover:border-[#8dc63f]/30 bg-white shadow-sm active:scale-95"
                >
                  Done
                </button>
              )}
            </div>
          )}

          <div className="max-h-60 overflow-y-auto custom-scrollbar p-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = selectedValues.includes(opt.value);
                return (
                  <div
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`
                      flex items-center justify-between px-3 py-2.5 rounded-xl text-sm cursor-pointer transition-all
                      ${isSelected ? 'bg-[#8dc63f]/10 text-[#76ab32] font-bold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                    `}
                  >
                    <span>{opt.label}</span>
                    {isSelected && <Check className="w-4 h-4 shrink-0" />}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center text-sm text-slate-400 italic">No results found</div>
            )}
          </div>
        </div>,
        document.body
      )}

      {error && <p className="text-xs font-semibold text-red-500 px-1 mt-0.5">{error}</p>}
    </div>
  );
};